/**
 * LearningHubV1 — Pupil Test-Ready Pack (May 26, 2026).
 *
 * Operator brief:
 *   "When an instructor/admin invites a pupil and they register
 *    through the trusted invite path, the pupil must land in a
 *    usable learner experience with no empty/dead education areas."
 *
 * This page is the V1 topic-based learning surface. It mounts a
 * 12-topic grid (content lives in `backend/learning_hub_v1.py`)
 * and renders a topic detail view when a card is tapped.
 *
 * Doctrine pins:
 *   • No empty/dead end paths — every link goes somewhere real.
 *   • Calm honest empty state if the homework list is empty.
 *   • Reads from /api/learning-hub-v1/* (read-only) and
 *     /api/homework-v1/my (learner-scoped).
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  PlayCircle,
  Eye,
  GitFork,
  RotateCw,
  Milestone,
  Gauge,
  Users,
  Move,
  Compass,
  CheckCircle,
  HelpCircle,
  BookOpen,
  Book,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LearnerBottomNav from "../components/learner/LearnerBottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  "play-circle": PlayCircle,
  "eye": Eye,
  "git-fork": GitFork,
  "rotate-cw": RotateCw,
  "milestone": Milestone,
  "gauge": Gauge,
  "users": Users,
  "move": Move,
  "compass": Compass,
  "check-circle": CheckCircle,
  "help-circle": HelpCircle,
  "steering-wheel": BookOpen, // fallback (no steering icon in lucide)
};

// Category label + order. Categories that aren't in this map fall to "general".
const CATEGORY_LABEL = {
  core_control: "Core control",
  junctions: "Junctions & negotiation",
  road_types: "Road types",
  manoeuvres: "Manoeuvres",
  independence: "Independence",
  test_prep: "Test preparation",
  mindset: "Mindset",
  general: "More",
};

export default function LearningHubV1() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  // A5 (Feb 27, 2026 exposure batch) — quiet current-stage micro-tile.
  // Single tile at the top of the topic catalogue when the learner has
  // a current responsibility stage. Re-uses /api/learner-framework/me
  // (the same endpoint StageLadder already calls). Silent on any error
  // or if no stage is current — never noisy, never a banner.
  const [currentStage, setCurrentStage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/api/learning-hub-v1/topics`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setTopics(r.data?.topics || []);
      })
      .catch(() => {
        if (!cancelled) setTopics([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Only relevant on the catalogue index (no slug). Silent on failure.
    if (slug) return;
    let cancelled = false;
    axios
      .get(`${API}/api/learner-framework/me`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const cs = r.data?.current_stage || null;
        // Defensive: only render when slug + label are both present.
        if (cs && cs.slug && cs.label) {
          setCurrentStage(cs);
        }
      })
      .catch(() => {
        // Calm silence — micro-tile simply does not render.
        if (!cancelled) setCurrentStage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (slug) {
    return <TopicDetail slug={slug} user={user} />;
  }

  return (
    <div
      data-testid="learning-hub-v1"
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24"
    >
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            data-testid="learning-hub-v1-back"
            onClick={() => nav("/learn")}
            className="p-2 -ml-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">
              Learning Hub
            </p>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">
              What would you like to work on?
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* A5 (Feb 27, 2026 exposure batch) — calm "current stage"
            micro-tile. Only renders when the learner has a current
            responsibility stage. One tap → /stage/{slug}. Honest
            counts. No banner, no badge, no nag. See
            /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §2.A5. */}
        {currentStage && (
          <button
            type="button"
            data-testid="learning-hub-v1-current-stage-tile"
            onClick={() => nav(`/stage/${currentStage.slug}`)}
            className="w-full text-left mb-4 rounded-2xl ring-1 ring-amber-200 bg-amber-50/60 p-4 hover:ring-amber-300 hover:shadow-sm transition active:scale-[0.99]"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-100 text-amber-700 inline-flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
                  Where you are
                </p>
                <p className="font-bold text-sm text-slate-900 mt-0.5">
                  {currentStage.label}
                </p>
                {typeof currentStage.lesson_count === "number" &&
                  currentStage.lesson_count > 0 && (
                    <p
                      data-testid="learning-hub-v1-current-stage-progress"
                      className="text-xs text-slate-600 mt-0.5"
                    >
                      {currentStage.completed_count || 0} of{" "}
                      {currentStage.lesson_count} lessons read
                    </p>
                  )}
              </div>
              <ChevronRight
                className="w-4 h-4 text-amber-400 mt-1"
                aria-hidden
              />
            </div>
          </button>
        )}

        {loading && (
          <div
            data-testid="learning-hub-v1-loading"
            className="text-sm text-slate-500"
          >
            Loading topics…
          </div>
        )}
        {!loading && topics.length === 0 && (
          <div
            data-testid="learning-hub-v1-empty"
            className="rounded-2xl ring-1 ring-slate-200 bg-white p-6 text-sm text-slate-500"
          >
            Learning topics couldn't load right now. Please try again in a moment.
          </div>
        )}
        {!loading && topics.length > 0 && (
          <div className="space-y-6">
            {/* Group topics by category so the 24-topic catalogue
                stays calm, not a wall. Operator-locked. */}
            {Array.from(
              topics.reduce((acc, t) => {
                const c = t.category || "general";
                if (!acc.has(c)) acc.set(c, []);
                acc.get(c).push(t);
                return acc;
              }, new Map())
            ).map(([cat, items]) => (
              <section key={cat} data-testid={`learning-hub-v1-cat-${cat}`}>
                <h2 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 px-1">
                  {CATEGORY_LABEL[cat] || cat}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {items.map((t) => {
                    const Icon = ICON_MAP[t.icon] || BookOpen;
                    return (
                      <button
                        key={t.slug}
                        data-testid={`learning-hub-v1-tile-${t.slug}`}
                        onClick={() => nav(`/learn/hub/${t.slug}`)}
                        className="text-left rounded-2xl ring-1 ring-slate-200 bg-white p-4 hover:ring-indigo-300 hover:shadow-md transition active:scale-[0.99]"
                      >
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 inline-flex items-center justify-center">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm text-slate-900">
                              {t.title}
                            </p>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                              {t.summary}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Things to explore (A4 — Feb 27, 2026 exposure batch).
            Calm slot-card chips for two existing, fully-built
            destinations previously unreachable from primary nav.
            Pure exposure — no new routes. Both targets are already
            <Route> entries in App.js. See
            /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §2.A4. */}
        {!loading && (
          <section
            data-testid="learning-hub-v1-explore"
            className="mt-6"
          >
            <h2 className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2 px-1">
              Things to explore
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="learning-hub-v1-explore-show-tell"
                onClick={() => nav("/learn/show-tell")}
                className="text-left rounded-2xl ring-1 ring-slate-200 bg-white p-4 hover:ring-emerald-300 hover:shadow-md transition active:scale-[0.99]"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                    <PlayCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-slate-900">Show &amp; Tell</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      Walk-through clips from real drives.
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 mt-1" aria-hidden />
                </div>
              </button>
              <button
                type="button"
                data-testid="learning-hub-v1-explore-highway-code"
                onClick={() => nav("/learn/highway-code")}
                className="text-left rounded-2xl ring-1 ring-slate-200 bg-white p-4 hover:ring-amber-300 hover:shadow-md transition active:scale-[0.99]"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-50 text-amber-600 inline-flex items-center justify-center">
                    <Book className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-slate-900">Highway Code</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      Read the rules in calm, plain language.
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 mt-1" aria-hidden />
                </div>
              </button>
            </div>
          </section>
        )}
      </main>

      <LearnerBottomNav active="learning" />
    </div>
  );
}

function TopicDetail({ slug, user }) {
  const nav = useNavigate();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myHomework, setMyHomework] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/learning-hub-v1/topics/${slug}`, {
        withCredentials: true,
      }),
      axios
        .get(`${API}/api/homework-v1/my`, { withCredentials: true })
        .catch(() => ({ data: { items: [] } })),
    ])
      .then(([t, h]) => {
        if (!cancelled) {
          setTopic(t.data || null);
          setMyHomework(
            (h.data?.items || []).filter((i) => i.topic_slug === slug)
          );
        }
      })
      .catch(() => {
        if (!cancelled) setTopic(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const submitFeedback = useCallback(
    async (hwId, status) => {
      try {
        await axios.post(
          `${API}/api/homework-v1/${hwId}/feedback`,
          { status },
          { withCredentials: true }
        );
        // refresh
        const h = await axios
          .get(`${API}/api/homework-v1/my`, { withCredentials: true })
          .catch(() => ({ data: { items: [] } }));
        setMyHomework(
          (h.data?.items || []).filter((i) => i.topic_slug === slug)
        );
      } catch (e) {
        // silent — surfaces will show stale state until next refresh
      }
    },
    [slug]
  );

  if (loading) {
    return (
      <div className="min-h-dvh bg-slate-50 dark:bg-[#0c1120] p-6 text-sm text-slate-500">
        Loading…
      </div>
    );
  }
  if (!topic) {
    return (
      <div className="min-h-dvh bg-slate-50 dark:bg-[#0c1120]">
        <div className="max-w-2xl mx-auto p-6">
          <button
            data-testid="topic-back-empty"
            onClick={() => nav("/learn/hub")}
            className="text-sm text-indigo-600 underline"
          >
            ← Back to Learning Hub
          </button>
          <p className="mt-4 text-sm text-slate-600">Topic not found.</p>
        </div>
      </div>
    );
  }

  const hw = topic.homework || {};
  const isRoundabouts = slug === "roundabouts";
  const rich = topic.rich_breakdown || null;

  return (
    <div
      data-testid={`learning-hub-v1-topic-${slug}`}
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24"
    >
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            data-testid={`topic-back-${slug}`}
            onClick={() => nav("/learn/hub")}
            className="p-2 -ml-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-slate-900 dark:text-white truncate">
            {topic.title}
          </h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        <Section testid="topic-summary" title="What it is" tone="indigo">
          <p className="text-sm text-slate-700 dark:text-slate-200">{topic.summary}</p>
        </Section>

        <Section testid="topic-why" title="Why it matters" tone="amber">
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {topic.why_it_matters}
          </p>
        </Section>

        <Section testid="topic-how" title="How to do it well" tone="emerald">
          <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1.5 list-disc pl-5">
            {(topic.what_good_looks_like || []).map((pt, i) => (
              <li key={i}>{pt}</li>
            ))}
          </ul>
        </Section>

        {isRoundabouts && rich && (
          <Section testid="topic-rich" title="Roundabout deep dive" tone="sky">
            <dl className="text-sm text-slate-700 dark:text-slate-200 space-y-2">
              <RichRow label="Give way to the right" value={rich.give_way_to_the_right} />
              <RichRow label="Approach speed" value={rich.approach_speed} />
              <RichRow label="Lane choice basics" value={rich.lane_choice_basics} />
              <RichRow label="Reading angle / intent" value={rich.reading_angle_and_intent} />
              <RichRow label="Spiral / multi-lane" value={rich.spiral_and_multi_lane} />
            </dl>
          </Section>
        )}

        <Section testid="topic-mistakes" title="Common mistakes" tone="rose">
          <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1.5 list-disc pl-5">
            {(isRoundabouts
              ? topic.common_learner_mistakes || topic.common_mistakes
              : topic.common_mistakes || []
            ).map((pt, i) => (
              <li key={i}>{pt}</li>
            ))}
          </ul>
        </Section>

        {Array.isArray(topic.self_check) && topic.self_check.length > 0 && (
          <Section testid="topic-self-check" title="Self-check" tone="sky">
            <ul className="text-sm text-slate-700 dark:text-slate-200 space-y-1.5 list-disc pl-5">
              {topic.self_check.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </Section>
        )}

        <Section testid="topic-homework" title="Homework task" tone="violet">
          <p className="font-bold text-sm text-slate-900 dark:text-white">
            {hw.title}
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">
            {hw.description}
          </p>
          {hw.deep_link && (
            <button
              data-testid={`topic-homework-deeplink-${slug}`}
              onClick={() => nav(hw.deep_link)}
              className="mt-3 text-xs font-bold text-indigo-600 underline"
            >
              Open the linked tool →
            </button>
          )}
        </Section>

        {/* Active homework on this topic — 4-state feedback */}
        {myHomework.length > 0 && (
          <Section
            testid="topic-my-homework"
            title="Your homework on this topic"
            tone="indigo"
          >
            <div className="space-y-3">
              {myHomework.map((h) => (
                <HomeworkRow
                  key={h.id}
                  homework={h}
                  onFeedback={(status) => submitFeedback(h.id, status)}
                />
              ))}
            </div>
          </Section>
        )}
      </main>

      <LearnerBottomNav active="learning" />
    </div>
  );
}

function Section({ testid, title, tone = "indigo", children }) {
  const tones = {
    indigo: "ring-indigo-100 bg-indigo-50/40",
    amber: "ring-amber-100 bg-amber-50/40",
    emerald: "ring-emerald-100 bg-emerald-50/40",
    rose: "ring-rose-100 bg-rose-50/40",
    violet: "ring-violet-100 bg-violet-50/40",
    sky: "ring-sky-100 bg-sky-50/40",
  };
  return (
    <section
      data-testid={testid}
      className={`rounded-2xl ring-1 bg-white dark:bg-slate-900 p-4 ${tones[tone] || tones.indigo}`}
    >
      <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
        {title}
      </p>
      {children}
    </section>
  );
}

function RichRow({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs font-bold text-slate-800 dark:text-slate-100">{label}</dt>
      <dd className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{value}</dd>
    </div>
  );
}

function HomeworkRow({ homework, onFeedback }) {
  const status = homework.status || "assigned";
  const FEEDBACK_OPTIONS = [
    { key: "understood", label: "Understood", tone: "emerald" },
    { key: "need_more_help", label: "Need more help", tone: "amber" },
    { key: "practised", label: "Practised", tone: "sky" },
    { key: "not_confident", label: "Not confident yet", tone: "rose" },
  ];
  const toneClass = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
  };
  return (
    <div
      data-testid={`hw-row-${homework.id}`}
      className="rounded-xl ring-1 ring-slate-200 bg-white p-3"
    >
      <p className="text-sm font-bold text-slate-900">{homework.task_title}</p>
      <p className="text-xs text-slate-600 mt-0.5">{homework.task_description}</p>
      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest">
        Current state: {status}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {FEEDBACK_OPTIONS.map((opt) => {
          const active = status === opt.key;
          return (
            <button
              key={opt.key}
              data-testid={`hw-feedback-${opt.key}-${homework.id}`}
              onClick={() => onFeedback(opt.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 transition ${
                active
                  ? `${toneClass[opt.tone]} ring-2`
                  : "bg-white text-slate-700 ring-slate-200 hover:ring-slate-400"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
